import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// CIR-2026-605: config is read from Vite's build-time env (VITE_-prefixed vars
// are inlined into import.meta.env by Vite/Vitest automatically — see
// .env.example). No key literals live in source. Fail fast rather than
// silently falling back to a hardcoded value if the build/test environment
// is misconfigured.
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    'Missing Supabase configuration: VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY must ' +
    'be set at build time. See .env.example for the required variables.'
  );
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  }
});

/**
 * Dedicated anon-only client for the auditor portal (CIR-2026-450).
 *
 * The auditor is authenticated by a bearer token that the server-side
 * token-resolving RPCs validate (CIR-2026-445: auditor_evidence,
 * auditor_dashboard_counts, validate_auditor_token, auditor_log_access) — not by
 * a Supabase auth session. The shared `supabase` singleton above persists any
 * logged-in staff/client JWT in localStorage and attaches it to every request,
 * so an auditor page opened while a staff/client user is signed in would run its
 * queries under THAT user's JWT (the `authenticated` role), not the `anon` role
 * the auditor path is designed for.
 *
 * This instance carries NO user session: `persistSession:false` gives it an
 * in-memory (not localStorage) session store, so it can never read the staff /
 * client `sb-<ref>-auth-token`; a distinct `storageKey` keeps it fully isolated
 * from the singleton and silences the multi-GoTrueClient warning; and
 * `autoRefreshToken`/`detectSessionInUrl` are off because there is no session to
 * refresh or recover. Every request therefore travels as `anon` with only the
 * publishable key — the auditor token is passed explicitly as an RPC argument
 * and resolved server-side, never trusted from the caller's identity.
 */
export const auditorSupabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    storageKey: 'sb-auditor-anon',
  }
});
